import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import React from 'react';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import { textContentProp } from 'utils/proptypes/modules/textProps';
import Image from 'components/Image';
import ButtonsList from 'components/buttons/ButtonsList';

const Div = styled('div')``;

const Content = styled('div')`
    /* Use .text-section to be  extra careful with !important-styles */
    &.text-section > * {
        /* Using :first-of-type or :last-of-type will not output the expectet result */
        :first-child {
            margin-top: 0 !important;
        }

        :last-child {
            margin-bottom: 0 !important;
        }
    }
`;

const Preamble = styled(Paragraph)`
    a {
        color: inherit;
    }
`;

const Text = styled(Paragraph)`
    /* Simulate margin-bottom 16px */
    /* This is better than magin-bottom: 16px; as that would add more margin than wanted */
    min-height: 16px;

    a {
        color: inherit;
    }
`;

/**
 * Display a large block with a image or video as a background and a revolving text in blendmode as a foreground.
 *
 * @param {object[]} textSections - Array of objects.
 * @param {string} textSections.layout - The type of content that should be display; heading, subHeading, ingress or paragraph.
 * @param {string} textSections.heading - The heading as a string. Used by heading and subHeading.
 * @param {string} textSections.headingTag - The html-tag used by heading and subHeading; h1, h2, h3 etc.
 * @param {array} textSections.text - Text as a wysiwyg-array. Used by ingress and paragraph.
 */

const TextSection = ({ customerServiceStyle = false, fullWidth = false, textSections }) =>
    textSections?.length > 0 ? (
        <Content className="text-section">
            {textSections.map((section, i) => {
                const { layout, heading, headingTag, text, image, buttons } = section;

                const key = `${layout}-${i}`;

                if (layout === 'heading') {
                    return (
                        <Heading
                            as={headingTag}
                            color={customerServiceStyle ? colors.grey6 : 'inherit'}
                            key={key}
                            my={customerServiceStyle ? ['32px', null, null, '48px'] : ['24px', null, '32px', '40px']}
                            size={customerServiceStyle ? 'md' : 'lg'}
                            width={fullWidth ? '100%' : ['100%', null, null, 'calc(75% - 12px)', 'calc(50% - 12px)']} // 12 - 9 - 6
                        >
                            {heading}
                        </Heading>
                    );
                } else if (layout === 'subHeading') {
                    return (
                        <Heading
                            as={headingTag}
                            fontWeight={500}
                            key={key}
                            m="16px 0"
                            size="xs"
                            width={fullWidth ? '100%' : ['100%', null, null, 'calc(75% - 12px)', 'calc(50% - 12px)']}
                        >
                            {heading}
                        </Heading>
                    );
                } else if (layout === 'ingress') {
                    return (
                        <Div
                            m={['24px 0 32px', null, '32px 0 40px', '40px 0 48px']}
                            width={fullWidth ? '100%' : ['100%', null, null, 'calc(75% - 12px)', 'calc(66.66% - 12px)']} // 12 - 9 - 8
                        >
                            <Wysiwyg
                                key={key}
                                data={text}
                                textComponent={props => (
                                    <Preamble size={customerServiceStyle ? 'lg' : 'xl'} {...props} />
                                )}
                                tagComponents={{ url: TextLinkWysisyg }}
                            />
                        </Div>
                    );
                } else if (layout === 'paragraph') {
                    return (
                        // 12 - 9 - 6
                        <Div
                            m="12px 0"
                            width={fullWidth ? '100%' : ['100%', null, null, 'calc(75% - 12px)', 'calc(50% - 12px)']}
                        >
                            <Wysiwyg
                                key={key}
                                data={text}
                                textComponent={props => <Text {...props} />}
                                tagComponents={{ url: TextLinkWysisyg }}
                            />
                        </Div>
                    );
                    } else if (layout === 'image') {
                        return (
                            <Image
                                alt={image.title}
                                display="block"
                                src={{ url: image.url, width: [375, 768, 1024, 1440, 1920] }}
                                sizes={fullWidth ? "50vw" : ['100vw', '50vw', '25vw']}
                                width={fullWidth ? ['100%', null, null, null, '75%'] : ['100%', null, '50%', '37.5%', '25%']}
                                height="auto"
                                m="12px 0"
                            />
                        );
                    } else if (layout === 'buttons') {
                        return (
                            <ButtonsList
                                m="24px -8px"
                                width={fullWidth ? '100%' : ['100%', null, null, 'calc(75% - 12px)', 'calc(50% - 12px)']}
                                buttons={buttons}
                            />
                        );
                    }

                return null;
            })}
        </Content>
    ) : null;

TextSection.propTypes = {
    customerServiceStyle: PropTypes.bool,
    fullWidth: PropTypes.bool,
    textSections: textContentProp,
};

export default TextSection;
